import { useEffect, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import {
  BUSINESS_MODES,
  extractAppVariantFromOrgData,
  extractBusinessModeFromOrgData,
  persistBusinessContext,
  readStoredBusinessContext,
} from "../utils/businessMode";
import { readBinding } from "../utils/storeBinding";

function resolveCandidateOrgId() {
  try {
    const binding = readBinding();
    const saved = JSON.parse(localStorage.getItem("installParams") || "null");
    return (
      binding?.orgId ||
      localStorage.getItem("orgId") ||
      localStorage.getItem("linkedOrgId") ||
      saved?.orgId ||
      ""
    );
  } catch {
    return localStorage.getItem("orgId") || localStorage.getItem("linkedOrgId") || "";
  }
}

export default function useResolvedBusinessMode() {
  const stored = readStoredBusinessContext();
  const [state, setState] = useState({
    businessMode: stored.businessMode || BUSINESS_MODES.HOSPITALITY_RETAIL,
    appVariant:
      stored.appVariant ||
      (stored.businessMode === BUSINESS_MODES.HOSPITALITY_RETAIL
        ? "shared_device"
        : "personal_device"),
    loading: true,
    source: "storage",
  });

  useEffect(() => {
    let cancelled = false;
    const fallbackTimer = window.setTimeout(() => {
      if (!cancelled) {
        setState((prev) => ({
          ...prev,
          loading: false,
          source: prev.source || "timeout-fallback",
        }));
      }
    }, 3500);

    async function run() {
      const orgId = resolveCandidateOrgId();
      if (!orgId) {
        if (!cancelled) {
          window.clearTimeout(fallbackTimer);
          setState((prev) => ({
            ...prev,
            loading: false,
            source: "default",
          }));
        }
        return;
      }

      try {
        const snap = await getDoc(doc(db, "orgs", orgId));
        if (!snap.exists()) {
          if (!cancelled) {
            window.clearTimeout(fallbackTimer);
            setState((prev) => ({
              ...prev,
              loading: false,
              source: "storage",
            }));
          }
          return;
        }

        const data = snap.data() || {};
        const businessMode = extractBusinessModeFromOrgData(data);
        const appVariant = extractAppVariantFromOrgData(data, businessMode);
        const persisted = persistBusinessContext({ businessMode, appVariant });

        if (!cancelled) {
          window.clearTimeout(fallbackTimer);
          setState({
            ...persisted,
            loading: false,
            source: "firestore",
          });
        }
      } catch (error) {
        console.warn("useResolvedBusinessMode failed to load org mode", error);
        if (!cancelled) {
          window.clearTimeout(fallbackTimer);
          setState((prev) => ({
            ...prev,
            loading: false,
            source: prev.source || "storage",
          }));
        }
      }
    }

    run();
    return () => {
      cancelled = true;
      window.clearTimeout(fallbackTimer);
    };
  }, []);

  return state;
}
